// contexts/PermissionContext.js
//
// Client-side mirror of the RBAC/PBAC engine's effective-permission bundle
// (GET /api/access/me/). The backend is always the real gate - every
// permission-guarded endpoint re-checks server-side - this context only
// drives what buttons/actions are shown so the UI matches what a click
// would actually be allowed to do.
import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import accessService from "../services/accessService";
import { useAuth } from "./AuthContext";

const PermissionContext = createContext({
  permissions: {},
  source: null,
  loading: true,
  can: () => true, // fail-open until the first successful fetch, to avoid flashing hidden buttons
  refresh: () => {},
});

export const PermissionProvider = ({ children }) => {
  const { caraUser } = useAuth();
  const [permissions, setPermissions] = useState({});
  const [source, setSource] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ready, setReady] = useState(false);

  const fetchPermissions = useCallback(async () => {
    if (!caraUser) {
      setPermissions({});
      setSource(null);
      setLoading(false);
      setReady(false);
      return;
    }
    setLoading(true);
    try {
      const data = await accessService.getMyAccess();
      setPermissions(data?.permissions || {});
      setSource(data?.source || null);
      setReady(true);
    } catch (error) {
      console.error("Failed to load effective permissions:", error);
      // Leave `ready` false so `can()` fails open rather than hiding every action
      // because of a transient network error.
    } finally {
      setLoading(false);
    }
  }, [caraUser]);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  const can = useCallback(
    (resourceId, action) => {
      if (!ready) return true; // fail-open before the bundle has loaded at least once
      const entry = permissions[resourceId];
      if (!entry) return false;
      return Array.isArray(entry.actions) && entry.actions.includes(action);
    },
    [permissions, ready]
  );

  const getDataScope = useCallback(
    (resourceId) => {
      const entry = permissions[resourceId];
      return entry?.data_scope || null;
    },
    [permissions]
  );

  const value = useMemo(
    () => ({ permissions, source, loading, can, getDataScope, refresh: fetchPermissions }),
    [permissions, source, loading, can, getDataScope, fetchPermissions]
  );

  return <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>;
};

export const usePermission = () => useContext(PermissionContext);

// Declarative guard: <Can resource="tprm.third_party_users" action="create">...</Can>
// Renders nothing while the bundle is loading for the very first time to avoid a
// flash of a button the user doesn't actually have, then renders children only if
// allowed. Pass `fallback` to render something else (e.g. a disabled button) instead.
export const Can = ({ resource, action, children, fallback = null }) => {
  const { can } = usePermission();
  return can(resource, action) ? children : fallback;
};

export default PermissionContext;
